/**
 * MinimoogPanel - Interface du Minimoog Model D
 * 3 oscillateurs, filtre ladder, 2 enveloppes
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { MINIMOOG_PRESETS } from '../../state/InstrumentPresets.js';

export class MinimoogPanel {
    constructor(minimoog, store, sequencer) {
        this.minimoog = minimoog;
        this.store = store;
        this.sequencer = sequencer;
        this.panelId = 'minimoog';

        this.components = {
            knobs: {},
            selects: {},
            stepButtons: []
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('minimoog');

        // Restaurer l'état power du synthé
        this.minimoog.setPower(isPowered);

        container.innerHTML = `
            <div class="machine minimoog ${isCollapsed ? 'collapsed' : ''} ${!isPowered ? 'powered-off' : ''}" id="minimoog_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="minimoog_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    MINIMOOG <span class="brand">Model D</span>
                    <div id="minimoog_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="minimoog_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="minimoog_content">
                <div class="minimoog-sections">
                    <!-- Oscillators -->
                    <div class="minimoog-section oscillators-section">
                        <div class="section-title">OSCILLATORS</div>
                        <div class="osc-row">
                            <div class="osc-group">
                                <div class="osc-label">OSC 1</div>
                                <div class="osc-controls" id="osc1_controls"></div>
                            </div>
                            <div class="osc-group">
                                <div class="osc-label">OSC 2</div>
                                <div class="osc-controls" id="osc2_controls"></div>
                            </div>
                            <div class="osc-group">
                                <div class="osc-label">OSC 3</div>
                                <div class="osc-controls" id="osc3_controls"></div>
                            </div>
                            <div class="osc-group noise-group">
                                <div class="osc-label">NOISE</div>
                                <div class="osc-controls" id="noise_controls"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Filter -->
                    <div class="minimoog-section filter-section">
                        <div class="section-title">FILTER</div>
                        <div class="filter-controls" id="filter_controls"></div>
                    </div>

                    <!-- Envelopes -->
                    <div class="minimoog-section envelopes-section">
                        <div class="env-group">
                            <div class="section-title">FILTER ENV</div>
                            <div class="env-controls" id="filter_env_controls"></div>
                        </div>
                        <div class="env-group">
                            <div class="section-title">AMP ENV</div>
                            <div class="env-controls" id="amp_env_controls"></div>
                        </div>
                    </div>

                    <!-- Master -->
                    <div class="minimoog-section master-section">
                        <div class="section-title">MASTER</div>
                        <div class="master-controls" id="master_controls"></div>
                    </div>
                </div>

                <!-- Sequencer -->
                <div class="sequencer machine-sequencer">
                    <div class="seq-header">
                        <div class="step-markers">
                            ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                        </div>
                        <div class="seq-speed-control">
                            <span class="speed-label">SPEED</span>
                            <select id="minimoog_speed" class="speed-select" title="Sequencer speed multiplier">
                                <option value="0.25">1/4x</option>
                                <option value="0.5">1/2x</option>
                                <option value="1" selected>1x</option>
                                <option value="2">2x</option>
                                <option value="4">4x</option>
                            </select>
                        </div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="minimoog_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="minimoog_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createOscillatorControls();
            this.createFilterControls();
            this.createEnvelopeControls();
            this.createMasterControls();
            this.createSequencer();
            this.setupSpeedControl();
            this.setupPresetSelector();
            this._loadParams();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('minimoog_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('minimoog_power');
        const machine = document.getElementById('minimoog_machine');

        powerBtn.addEventListener('click', () => {
            const isOn = this.minimoog.getPower();
            const newState = !isOn;
            this.minimoog.setPower(newState);
            this.store.setPowerState('minimoog', newState);

            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPresetSelector() {
        const container = document.getElementById('minimoog_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: MINIMOOG_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.minimoog.setParams(presetData);
        this._updateControlsFromParams();
        this._saveParams();
    }

    _updateControlsFromParams() {
        const params = this.minimoog.getParams();

        // Update waveform selectors
        ['osc1', 'osc2', 'osc3'].forEach((osc) => {
            const selector = this.components.selects[`${osc}Wave`];
            if (selector && params[`${osc}Waveform`]) {
                selector.setValue(params[`${osc}Waveform`]);
            }
        });

        // Update knobs
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined && knob?.setValue) {
                knob.setValue(params[key]);
            }
        });
    }

    createOscillatorControls() {
        const params = this.minimoog.getParams();

        // OSC 1
        const osc1Container = document.getElementById('osc1_controls');
        this._createOscControls(osc1Container, 1, params);

        // OSC 2
        const osc2Container = document.getElementById('osc2_controls');
        this._createOscControls(osc2Container, 2, params);

        // OSC 3
        const osc3Container = document.getElementById('osc3_controls');
        this._createOscControls(osc3Container, 3, params);

        // Noise
        const noiseContainer = document.getElementById('noise_controls');
        this.components.knobs.noiseLevel = themeManager.createKnob(noiseContainer, {
            min: 0, max: 1, value: params.noiseLevel,
            label: 'Level', step: 0.01
        }).onChange(value => {
            this.minimoog.setNoiseLevel(value);
            this._saveParams();
        });
    }

    _createOscControls(container, oscNum, params) {
        const prefix = `osc${oscNum}`;

        // Waveform selector avec boutons
        this.components.selects[`${prefix}Wave`] = new WaveSelector(container, {
            waveforms: [
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'SQR' },
                { value: 'triangle', label: 'TRI' },
                { value: 'sine', label: 'SIN' }
            ],
            value: params[`${prefix}Waveform`],
            label: 'Wave',
            idPrefix: `minimoog_${prefix}`
        }).onChange(value => {
            this.minimoog[`setOsc${oscNum}Waveform`](value);
            this._saveParams();
        });

        // Octave
        this.components.knobs[`${prefix}Octave`] = themeManager.createKnob(container, {
            min: -2, max: 2, value: params[`${prefix}Octave`],
            label: 'Octave', step: 1
        }).onChange(value => {
            this.minimoog[`setOsc${oscNum}Octave`](Math.round(value));
            this._saveParams();
        });

        // Tune (detune in cents)
        this.components.knobs[`${prefix}Tune`] = themeManager.createKnob(container, {
            min: -100, max: 100, value: params[`${prefix}Tune`],
            label: 'Tune', step: 1
        }).onChange(value => {
            this.minimoog[`setOsc${oscNum}Tune`](value);
            this._saveParams();
        });

        // Level
        this.components.knobs[`${prefix}Level`] = themeManager.createKnob(container, {
            min: 0, max: 1, value: params[`${prefix}Level`],
            label: 'Level', step: 0.01
        }).onChange(value => {
            this.minimoog[`setOsc${oscNum}Level`](value);
            this._saveParams();
        });
    }

    createFilterControls() {
        const container = document.getElementById('filter_controls');
        const params = this.minimoog.getParams();

        // Cutoff
        this.components.knobs.cutoff = themeManager.createKnob(container, {
            min: 20, max: 20000, value: params.cutoff,
            label: 'Cutoff', step: 1
        }).onChange(value => {
            this.minimoog.setCutoff(value);
            this._saveParams();
        });

        // Resonance
        this.components.knobs.resonance = themeManager.createKnob(container, {
            min: 0.5, max: 20, value: params.resonance,
            label: 'Resonance', step: 0.1
        }).onChange(value => {
            this.minimoog.setResonance(value);
            this._saveParams();
        });

        // Env Amount
        this.components.knobs.filterEnvAmount = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.filterEnvAmount,
            label: 'Env Amt', step: 0.01
        }).onChange(value => {
            this.minimoog.setFilterEnvAmount(value);
            this._saveParams();
        });

        // Key Track
        this.components.knobs.filterKeyTrack = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.filterKeyTrack,
            label: 'Key Trk', step: 0.01
        }).onChange(value => {
            this.minimoog.setFilterKeyTrack(value);
            this._saveParams();
        });
    }

    createEnvelopeControls() {
        const params = this.minimoog.getParams();

        // Filter Envelope
        const filterEnvContainer = document.getElementById('filter_env_controls');

        this.components.knobs.filterAttack = themeManager.createKnob(filterEnvContainer, {
            min: 0.001, max: 2, value: params.filterAttack,
            label: 'Attack', step: 0.001
        }).onChange(value => {
            this.minimoog.setFilterAttack(value);
            this._saveParams();
        });

        this.components.knobs.filterDecay = themeManager.createKnob(filterEnvContainer, {
            min: 0.01, max: 3, value: params.filterDecay,
            label: 'Decay', step: 0.01
        }).onChange(value => {
            this.minimoog.setFilterDecay(value);
            this._saveParams();
        });

        this.components.knobs.filterSustain = themeManager.createKnob(filterEnvContainer, {
            min: 0, max: 1, value: params.filterSustain,
            label: 'Sustain', step: 0.01
        }).onChange(value => {
            this.minimoog.setFilterSustain(value);
            this._saveParams();
        });

        // Amp Envelope
        const ampEnvContainer = document.getElementById('amp_env_controls');

        this.components.knobs.ampAttack = themeManager.createKnob(ampEnvContainer, {
            min: 0.001, max: 2, value: params.ampAttack,
            label: 'Attack', step: 0.001
        }).onChange(value => {
            this.minimoog.setAmpAttack(value);
            this._saveParams();
        });

        this.components.knobs.ampDecay = themeManager.createKnob(ampEnvContainer, {
            min: 0.01, max: 3, value: params.ampDecay,
            label: 'Decay', step: 0.01
        }).onChange(value => {
            this.minimoog.setAmpDecay(value);
            this._saveParams();
        });

        this.components.knobs.ampSustain = themeManager.createKnob(ampEnvContainer, {
            min: 0, max: 1, value: params.ampSustain,
            label: 'Sustain', step: 0.01
        }).onChange(value => {
            this.minimoog.setAmpSustain(value);
            this._saveParams();
        });

        this.components.knobs.ampRelease = themeManager.createKnob(ampEnvContainer, {
            min: 0.01, max: 5, value: params.ampRelease,
            label: 'Release', step: 0.01
        }).onChange(value => {
            this.minimoog.setAmpRelease(value);
            this._saveParams();
        });
    }

    createMasterControls() {
        const container = document.getElementById('master_controls');
        const params = this.minimoog.getParams();

        // Glide
        this.components.knobs.glide = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.glide,
            label: 'Glide', step: 0.01
        }).onChange(value => {
            this.minimoog.setGlide(value);
            this._saveParams();
        });

        // Volume
        this.components.knobs.volume = themeManager.createKnob(container, {
            min: 0, max: 1, value: params.volume,
            label: 'Volume', step: 0.01
        }).onChange(value => {
            this.minimoog.setVolume(value);
            this._saveParams();
        });
    }

    createSequencer() {
        const notesContainer = document.getElementById('minimoog_notes');
        const gatesContainer = document.getElementById('minimoog_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.minimoogNotes[i] || 'C2',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('minimoogNotes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.minimoogGates[i] || false
            }).onChange(active => {
                this.store.setStep('minimoogGates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    setupSpeedControl() {
        const speedSelect = document.getElementById('minimoog_speed');
        if (!speedSelect) return;

        // Load current value from store
        const currentSpeed = this.store.getSynthParam('minimoog', 'seqSpeed') || 1;
        speedSelect.value = currentSpeed.toString();

        // Handle changes
        speedSelect.addEventListener('change', (e) => {
            const speed = parseFloat(e.target.value);
            this.store.setSynthParam('minimoog', 'seqSpeed', speed);
        });
    }

    _saveParams() {
        if (this.store) {
            this.store.setSynthParams('minimoog', this.minimoog.getParams());
        }
    }

    _loadParams() {
        const saved = this.store?.getSynthParams('minimoog');
        if (saved) {
            this.minimoog.setParams(saved);
            this._updateControlsFromParams();
        }
    }

    updateFromParams() {
        const params = this.minimoog.getParams();
        // Update all knobs from params
        Object.entries(this.components.knobs).forEach(([key, knob]) => {
            if (params[key] !== undefined && knob) {
                knob.setValue(params[key]);
            }
        });
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy());
        Object.values(this.components.selects).forEach(s => s?.destroy());
        this.components.stepButtons.forEach(b => b?.destroy());
    }
}
